import { ExternalLink } from "lucide-react";
import React, { createContext, useContext } from "react";
import { getTicketKeys, jiraBrowseUrl } from "../jiraTickets";
import { Tooltip } from "./Tooltip";

// Provided by the Dashboard so a badge click can filter (via the search box)
// from any view without threading a callback through every card.
export const TicketFilterContext = createContext(null);

/**
 * The MR's primary Jira ticket key: click to filter the dashboard to it, or
 * use the arrow to open it in Jira. Renders nothing when the MR's title and
 * source branch reference no ticket.
 */
export const JiraBadge = ({ mr }) => {
	const onSelectTicket = useContext(TicketFilterContext);
	const [primaryKey, ...otherKeys] = getTicketKeys(mr);
	if (!primaryKey) return null;

	const stop = (e) => e.stopPropagation();

	return (
		<span className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[var(--color-text-secondary)]">
			<Tooltip content={onSelectTicket ? `Filter to ${primaryKey}` : null}>
				<button
					type="button"
					draggable="false"
					disabled={!onSelectTicket}
					onClick={(e) => {
						stop(e);
						onSelectTicket(primaryKey);
					}}
					className="rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1.5 font-mono text-[11px] leading-4 hover:border-[var(--color-accent)] hover:text-[var(--color-text-primary)] transition-colors disabled:cursor-default"
				>
					{primaryKey}
				</button>
			</Tooltip>
			{otherKeys.length > 0 && (
				<Tooltip content={`Also references ${otherKeys.join(", ")}`}>
					<span
						// biome-ignore lint/a11y/noNoninteractiveTabindex: focus reveals the tooltip for keyboard users.
						tabIndex={0}
						aria-label={`Also references ${otherKeys.join(", ")}`}
						className="text-[11px] tabular-nums"
					>
						+{otherKeys.length}
					</span>
				</Tooltip>
			)}
			<a
				href={jiraBrowseUrl(primaryKey)}
				target="_blank"
				rel="noopener noreferrer"
				draggable="false"
				onClick={stop}
				aria-label={`Open ${primaryKey} in Jira`}
				title={`Open ${primaryKey} in Jira`}
				className="rounded p-0.5 hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
			>
				<ExternalLink className="h-3 w-3" />
			</a>
		</span>
	);
};
